export const LOCALES = ['en', 'zh-Hans', 'zh-Hant', 'es'] as const;
export const APPEARANCE_PALETTE = {
  shirt: ['#4c8582', '#bd795d', '#6b8ca0', '#aa8eaf', '#c7a052', '#596071'],
  skin: ['#ba8563', '#e1b899', '#c58a63', '#9f6d50', '#7f513b', '#593b30'],
  hair: ['#3b2b23', '#49382e', '#282a2c', '#b08851'],
  trousers: ['#42505e', '#4c5559', '#665c50', '#384743'],
} as const;
export type Locale = (typeof LOCALES)[number];
export type Localized = Record<Locale, string>;
export const text = (en: string, cn: string, tw: string, es: string): Localized => ({
  en,
  'zh-Hans': cn,
  'zh-Hant': tw,
  es,
});
export type ZoneId = 'brooklyn' | 'midtown' | 'lower';
export type SkillId = 'service' | 'logic' | 'creative';
export const CITY_ID = 'us-nyc';
export const RULES_VERSION = 'nyc-2026-11-03-v1.3';
// Gross NPC issuance per paid allowance stays below the maximum C$150 wage.
export const NPC_DEMAND: Record<12 | 18 | 24, number> = { 12: 12, 18: 8, 24: 5 };
export const DAILY_SHIFTS = 3;
export const WORK_SECONDS = 45;
export const PRICE_LEVELS = [12, 18, 24] as const;
export const zones = [
  {
    id: 'brooklyn' as const,
    name: text('Brooklyn', '布鲁克林', '布魯克林', 'Brooklyn'),
    subtitle: text(
      'A place to call home',
      '找到属于自己的生活',
      '找到屬於自己的生活',
      'Un lugar para llamar hogar',
    ),
    focus: [-8, 0] as const,
  },
  {
    id: 'midtown' as const,
    name: text('Midtown', '曼哈顿中城', '曼哈頓中城', 'Midtown'),
    subtitle: text(
      'Small steps. Bigger dreams.',
      '从小目标走向大梦想',
      '從小目標走向大夢想',
      'Pequeños pasos, grandes sueños.',
    ),
    focus: [8, -3] as const,
  },
  {
    id: 'lower' as const,
    name: text('Lower Manhattan', '曼哈顿下城', '曼哈頓下城', 'Bajo Manhattan'),
    subtitle: text(
      'Make your mark on the city',
      '在城市里留下你的改变',
      '在城市裡留下你的改變',
      'Deja tu huella en la ciudad',
    ),
    focus: [0, 1] as const,
  },
];

export const locations = [
  {
    id: 'home',
    zone: 'brooklyn',
    x: -13.5,
    z: -3.5,
    icon: 'home',
    name: text('Your brownstone', '你的公寓', '你的公寓', 'Tu apartamento'),
    description: text(
      'Rest, make a meal, and make this little place yours.',
      '休息、做饭，把小房间布置成你喜欢的样子。',
      '休息、做飯，把小房間布置成你喜歡的樣子。',
      'Descansa, cocina y dale tu estilo a este pequeño hogar.',
    ),
    actions: ['rest', 'cook', 'decorate'],
  },
  {
    id: 'bodega',
    zone: 'brooklyn',
    x: 0,
    z: -3.5,
    icon: 'shopping-bag',
    name: text('Stoop & Basket', '门廊杂货铺', '門廊雜貨鋪', 'Stoop & Basket'),
    description: text(
      'A sandwich, a familiar face, and a little neighborhood news.',
      '买份三明治，认识邻居，听听街区的新鲜事。',
      '買份三明治，認識鄰居，聽聽街區的新鮮事。',
      'Un sándwich, caras conocidas y noticias del barrio.',
    ),
    actions: ['sandwich', 'snack', 'npc'],
  },
  {
    id: 'laundry',
    zone: 'brooklyn',
    x: -20,
    z: 6.5,
    icon: 'sparkles',
    name: text('Spin Cycle', '转角洗衣房', '轉角洗衣房', 'Spin Cycle'),
    description: text(
      'Help a neighbor and turn a chore into a story.',
      '帮邻居一个小忙，让日常琐事变成一段故事。',
      '幫鄰居一個小忙，讓日常瑣事變成一段故事。',
      'Ayuda a un vecino y convierte una tarea en una historia.',
    ),
    actions: ['neighbor-help', 'npc'],
  },
  {
    id: 'park',
    zone: 'brooklyn',
    x: 12,
    z: 4.3,
    icon: 'trees',
    name: text('East River Green', '东河小公园', '東河小公園', 'East River Green'),
    description: text(
      'Free fresh air, a river view, and a project we can build together.',
      '免费散步、看河景，也为街区共同出一份力。',
      '免費散步、看河景，也為街區共同出一份力。',
      'Aire libre, vistas al río y un proyecto para construir juntos.',
    ),
    actions: ['walk', 'community', 'photo'],
  },
  {
    id: 'cafe',
    zone: 'midtown',
    x: -7,
    z: -3.5,
    icon: 'coffee',
    name: text('Corner & Co.', '街角咖啡馆', '街角咖啡館', 'Corner & Co.'),
    description: text(
      'Your first shift could be the start of something.',
      '从第一份工作，开始你的纽约生活。',
      '從第一份工作，開始你的紐約生活。',
      'Tu primer turno puede ser el comienzo de algo.',
    ),
    actions: ['coffee', 'work-service', 'npc'],
  },
  {
    id: 'jobs',
    zone: 'midtown',
    x: 16,
    z: -3.5,
    icon: 'briefcase-business',
    name: text('The Next Chapter', '下一站职业中心', '下一站職業中心', 'The Next Chapter'),
    description: text(
      'Try a new career. Learn something. Grow at your own pace.',
      '试试新职业，练习技能，按自己的节奏成长。',
      '試試新職業，練習技能，按自己的節奏成長。',
      'Prueba una carrera, aprende y crece a tu ritmo.',
    ),
    actions: ['work-logic', 'train-logic', 'careers'],
  },
  {
    id: 'bryant',
    zone: 'midtown',
    x: 20,
    z: 7,
    icon: 'leaf',
    name: text('Bryant Park', '布莱恩特公园', '布萊恩特公園', 'Bryant Park'),
    description: text(
      'A lunch break with room for a new friend.',
      '午休时停一停，也许会遇到新朋友。',
      '午休時停一停，也許會遇到新朋友。',
      'Un descanso con espacio para una nueva amistad.',
    ),
    actions: ['walk', 'photo', 'npc'],
  },
  {
    id: 'pizza',
    zone: 'lower',
    x: 6.2,
    z: -3.5,
    icon: 'pizza',
    name: text('One More Slice', '再来一片披萨', '再來一片披薩', 'One More Slice'),
    description: text(
      'Grab a slice or lend a hand in the kitchen.',
      '买片披萨，或到厨房帮忙，练习服务技能。',
      '買片披薩，或到廚房幫忙，練習服務技能。',
      'Come una porción o echa una mano en la cocina.',
    ),
    actions: ['pizza', 'work-service', 'train-service'],
  },
  {
    id: 'studio',
    zone: 'lower',
    x: 15,
    z: 8,
    icon: 'palette',
    name: text('Open Door Studio', '开门创作室', '開門創作室', 'Open Door Studio'),
    description: text(
      'A creative brief, a new skill, and a chance to be seen.',
      '完成一个创作任务，练习技能，让作品被看见。',
      '完成一個創作任務，練習技能，讓作品被看見。',
      'Un encargo creativo, una habilidad y una oportunidad.',
    ),
    actions: ['work-creative', 'train-creative', 'photo'],
  },
  {
    id: 'city-hall',
    zone: 'lower',
    x: -15.7,
    z: 2,
    icon: 'landmark',
    name: text('Civic Hall', '城市议事厅', '城市議事廳', 'Civic Hall'),
    description: text(
      'Our in-game city. Our shared next chapter.',
      '了解游戏内政策、社区项目与虚构选举。',
      '了解遊戲內政策、社區項目與虛構選舉。',
      'Políticas, proyectos comunitarios y elecciones ficticias.',
    ),
    actions: ['government', 'community', 'npc'],
  },
] as const;
export type LocationId = (typeof locations)[number]['id'];

export const careers = [
  {
    id: 'service' as SkillId,
    name: text('Hospitality', '餐饮服务', '餐飲服務', 'Hostelería'),
    color: '#c68559',
    stages: [
      text('Counter starter', '柜台新人', '櫃檯新人', 'Ayudante'),
      text('Shift lead', '值班负责人', '值班負責人', 'Encargado de turno'),
      text('Neighborhood favorite', '街区招牌', '街區招牌', 'Favorito del barrio'),
    ],
    prompt: text(
      'The morning rush is here. What comes first?',
      '早高峰来了，先做哪件事？',
      '早高峰來了，先做哪件事？',
      'Llegó la hora punta. ¿Qué haces primero?',
    ),
    choices: [
      text('Confirm the order', '确认订单', '確認訂單', 'Confirmar el pedido'),
      text('Prepare the workspace', '整理操作台', '整理操作台', 'Preparar la estación'),
      text('Greet the next customer', '招呼下一位顾客', '招呼下一位顧客', 'Saludar al siguiente cliente'),
    ],
  },
  {
    id: 'logic' as SkillId,
    name: text('Office & tech', '办公室与技术', '辦公室與技術', 'Oficina y tecnología'),
    color: '#6c91a0',
    stages: [
      text('Project assistant', '项目助理', '專案助理', 'Asistente'),
      text('Problem solver', '问题解决者', '問題解決者', 'Especialista'),
      text('Team specialist', '团队骨干', '團隊骨幹', 'Referente del equipo'),
    ],
    prompt: text(
      'A client changed the brief. What is your next step?',
      '客户更改了需求，下一步怎么做？',
      '客戶更改了需求，下一步怎麼做？',
      'El cliente cambió el encargo. ¿Cuál es tu siguiente paso?',
    ),
    choices: [
      text('Clarify the goal', '澄清目标', '釐清目標', 'Aclarar el objetivo'),
      text('Break it into steps', '拆成小步骤', '拆成小步驟', 'Dividirlo en pasos'),
      text('Check the result', '检查结果', '檢查結果', 'Revisar el resultado'),
    ],
  },
  {
    id: 'creative' as SkillId,
    name: text('Creative freelance', '创意自由职业', '創意自由職業', 'Trabajo creativo'),
    color: '#a387af',
    stages: [
      text('Fresh perspective', '创作新面孔', '創作新面孔', 'Nueva perspectiva'),
      text('Trusted creator', '可靠创作者', '可靠創作者', 'Creador de confianza'),
      text('Local original', '本地原创之星', '本地原創之星', 'Talento local'),
    ],
    prompt: text(
      'The block needs a new event poster. Pick a direction.',
      '街区活动需要新海报，选一个方向。',
      '街區活動需要新海報，選一個方向。',
      'El barrio necesita un cartel. Elige una dirección.',
    ),
    choices: [
      text('Warm and welcoming', '温暖友好', '溫暖友好', 'Cálido y acogedor'),
      text('Bold and colorful', '鲜明多彩', '鮮明多彩', 'Audaz y colorido'),
      text('Clean and simple', '简洁清楚', '簡潔清楚', 'Claro y sencillo'),
    ],
  },
];

export const activities = {
  rest: {
    name: text('Take a real break', '好好休息', '好好休息', 'Descansar'),
    cost: 0,
    energy: 45,
    hunger: -2,
    cooldown: 60,
    icon: 'bed-double',
  },
  cook: {
    name: text('Make a simple meal', '做份家常餐', '做份家常餐', 'Preparar algo sencillo'),
    cost: 0,
    energy: -2,
    hunger: 45,
    cooldown: 60,
    icon: 'cooking-pot',
  },
  sandwich: {
    name: text('Deli sandwich', '熟食三明治', '熟食三明治', 'Sándwich'),
    cost: 12,
    energy: 4,
    hunger: 65,
    cooldown: 10,
    icon: 'sandwich',
  },
  snack: {
    name: text('A little snack', '来点小零食', '來點小零食', 'Un tentempié'),
    cost: 5,
    energy: 2,
    hunger: 25,
    cooldown: 10,
    icon: 'apple',
  },
  coffee: {
    name: text('Your usual coffee', '一杯熟悉的咖啡', '一杯熟悉的咖啡', 'Tu café de siempre'),
    cost: 8,
    energy: 30,
    hunger: 0,
    cooldown: 10,
    icon: 'coffee',
  },
  pizza: {
    name: text('A New York slice', '一片纽约披萨', '一片紐約披薩', 'Una porción neoyorquina'),
    cost: 10,
    energy: 5,
    hunger: 55,
    cooldown: 10,
    icon: 'pizza',
  },
  walk: {
    name: text('Take the scenic route', '沿着风景走一走', '沿著風景走一走', 'Dar un paseo'),
    cost: 0,
    energy: 12,
    hunger: -3,
    cooldown: 45,
    icon: 'footprints',
  },
  'neighbor-help': {
    name: text('Help a neighbor', '帮邻居一个忙', '幫鄰居一個忙', 'Ayudar a un vecino'),
    cost: 0,
    energy: -8,
    hunger: -3,
    cooldown: 120,
    icon: 'heart-handshake',
  },
  'train-service': {
    name: text('Practice your craft', '练习服务技巧', '練習服務技巧', 'Practicar el oficio'),
    cost: 10,
    energy: -8,
    hunger: -4,
    cooldown: 90,
    skill: 'service',
    icon: 'chef-hat',
  },
  'train-logic': {
    name: text('Solve a small challenge', '解决一个小挑战', '解決一個小挑戰', 'Resolver un reto'),
    cost: 10,
    energy: -8,
    hunger: -4,
    cooldown: 90,
    skill: 'logic',
    icon: 'lightbulb',
  },
  'train-creative': {
    name: text('Make something new', '做一个小创作', '做一個小創作', 'Crear algo nuevo'),
    cost: 10,
    energy: -8,
    hunger: -4,
    cooldown: 90,
    skill: 'creative',
    icon: 'paintbrush',
  },
} as const;
export type ActivityId = keyof typeof activities;

export const furniture = [
  ['plant', 'A little green', '一抹绿意', '一抹綠意', 'Un poco de verde', 35, '#82905d', 'plant'],
  ['rug', 'Sunrise rug', '晨光地毯', '晨光地毯', 'Alfombra amanecer', 60, '#c88b64', 'rug'],
  ['lamp', 'Reading lamp', '阅读落地灯', '閱讀落地燈', 'Lámpara de lectura', 55, '#dfb362', 'lamp'],
  ['books', 'Weekend books', '周末书架', '週末書架', 'Libros de fin de semana', 65, '#9c7051', 'shelf'],
  ['chair', 'Cozy armchair', '舒适单人椅', '舒適單人椅', 'Sillón acogedor', 90, '#6d9290', 'chair'],
  ['sofa', 'Sunday sofa', '星期日沙发', '星期日沙發', 'Sofá de domingo', 180, '#caa083', 'sofa'],
  [
    'desk',
    'First chapter desk',
    '新篇章书桌',
    '新篇章書桌',
    'Escritorio del comienzo',
    120,
    '#a97750',
    'desk',
  ],
  ['poster', 'City print', '城市装饰画', '城市裝飾畫', 'Lámina de la ciudad', 40, '#6f949e', 'art'],
  ['table', 'Breakfast table', '早餐小圆桌', '早餐小圓桌', 'Mesa de desayuno', 85, '#b17a53', 'table'],
  ['flowers', 'Fresh flowers', '一束鲜花', '一束鮮花', 'Flores frescas', 30, '#c68c99', 'plant'],
  ['guitar', 'Apartment guitar', '公寓小吉他', '公寓小吉他', 'Guitarra', 140, '#be8b4c', 'guitar'],
  ['clock', 'Slow Sunday clock', '慢周末挂钟', '慢週末掛鐘', 'Reloj de domingo', 45, '#e6d5b5', 'art'],
  ['mirror', 'Morning mirror', '晨间穿衣镜', '晨間穿衣鏡', 'Espejo', 80, '#b9c6bb', 'mirror'],
  ['pillows', 'Color pop cushions', '彩色靠垫', '彩色靠墊', 'Cojines de color', 35, '#c78561', 'pillows'],
  ['teal-rug', 'River rug', '河流地毯', '河流地毯', 'Alfombra del río', 65, '#598f91', 'rug'],
  [
    'bookcase',
    'Big ideas bookcase',
    '大想法书柜',
    '大想法書櫃',
    'Estantería de ideas',
    150,
    '#855f48',
    'shelf',
  ],
  ['coffee-cart', 'Coffee corner', '家中咖啡角', '家中咖啡角', 'Rincón del café', 110, '#a47755', 'desk'],
  ['floor-plant', 'Tall fiddle leaf', '高脚绿植', '高腳綠植', 'Planta alta', 80, '#677f4e', 'plant'],
  ['record', 'Record corner', '唱片角落', '唱片角落', 'Rincón de discos', 130, '#ac876c', 'desk'],
  ['skyline', 'Skyline canvas', '天际线画布', '天際線畫布', 'Lienzo de Manhattan', 100, '#698b9d', 'art'],
  ['bench', 'Window bench', '窗边长凳', '窗邊長凳', 'Banco de ventana', 90, '#a68262', 'bench'],
  ['stool', 'Kitchen stool', '厨房高脚凳', '廚房高腳凳', 'Taburete', 35, '#a87d55', 'chair'],
  ['nightstand', 'Bedside table', '床边小柜', '床邊小櫃', 'Mesita de noche', 55, '#947353', 'desk'],
  ['welcome', 'Welcome mat', '欢迎地垫', '歡迎地墊', 'Felpudo de bienvenida', 25, '#b89263', 'rug'],
].map(([id, en, cn, tw, es, price, color, kind]) => ({
  id: id as string,
  name: text(en as string, cn as string, tw as string, es as string),
  price: price as number,
  color: color as string,
  kind: kind as string,
}));

export const homes = [
  {
    id: 'starter',
    name: text('Your first room', '第一间小屋', '第一間小屋', 'Tu primera habitación'),
    cost: 0,
    slots: 8,
  },
  {
    id: 'studio',
    name: text('Sunlit studio', '阳光单间', '陽光套房', 'Estudio soleado'),
    cost: 600,
    slots: 14,
  },
  {
    id: 'loft',
    name: text('A room to grow', '梦想小阁楼', '夢想小閣樓', 'Espacio para crecer'),
    cost: 1800,
    slots: 20,
  },
];
export const npcs = [
  {
    id: 'maya',
    name: 'Maya',
    role: text('Café manager · NPC', '咖啡馆经理 · NPC', '咖啡館經理 · NPC', 'Gerente de café · NPC'),
    location: 'cafe',
    color: '#b47557',
    greeting: text(
      'Big dreams start with a small first step. Ready for your first shift?',
      '大梦想可以从一小步开始。准备好上第一班了吗？',
      '大夢想可以從一小步開始。準備好上第一班了嗎？',
      'Los grandes sueños empiezan con un pequeño paso. ¿Listo para tu primer turno?',
    ),
  },
  {
    id: 'sam',
    name: 'Sam',
    role: text('Neighborhood grocer · NPC', '邻里店员 · NPC', '鄰里店員 · NPC', 'Tendero del barrio · NPC'),
    location: 'bodega',
    color: '#6d8e8a',
    greeting: text(
      'A good sandwich and a familiar face. That is a pretty good start.',
      '一份三明治、一张熟悉的面孔，就是不错的一天。',
      '一份三明治、一張熟悉的面孔，就是不錯的一天。',
      'Un buen sándwich y una cara conocida: un gran comienzo.',
    ),
  },
  {
    id: 'alex',
    name: 'Alex',
    role: text('Career guide · NPC', '职业顾问 · NPC', '職業顧問 · NPC', 'Orientador laboral · NPC'),
    location: 'jobs',
    color: '#6a8f9c',
    greeting: text(
      'You do not have to choose your whole life today. Just choose your next step.',
      '今天不用决定整个人生，先选下一步就好。',
      '今天不用決定整個人生，先選下一步就好。',
      'No tienes que decidir toda tu vida hoy. Solo el siguiente paso.',
    ),
  },
  {
    id: 'rosa',
    name: 'Rosa',
    role: text('Housing helper · NPC', '住房助手 · NPC', '住房助手 · NPC', 'Asesora de vivienda · NPC'),
    location: 'home',
    color: '#ae859a',
    greeting: text(
      'Your basic room is always here for you. Make yourself at home.',
      '基础房间一直为你保留。慢慢把它变成自己的家吧。',
      '基礎房間一直為你保留。慢慢把它變成自己的家吧。',
      'Tu habitación básica siempre estará aquí. Ponte cómodo.',
    ),
  },
  {
    id: 'jordan',
    name: 'Jordan',
    role: text('Your neighbor · NPC', '你的邻居 · NPC', '你的鄰居 · NPC', 'Tu vecino · NPC'),
    location: 'park',
    color: '#bc985c',
    greeting: text(
      'We are making the riverfront a little greener. Even one person can help.',
      '我们在让河边变得更绿一点。一个人也能出一份力。',
      '我們在讓河邊變得更綠一點。一個人也能出一份力。',
      'Estamos haciendo más verde la ribera. Una persona también cuenta.',
    ),
  },
  {
    id: 'casey',
    name: 'Casey',
    role: text(
      'Civic coordinator · NPC',
      '公共事务协调员 · NPC',
      '公共事務協調員 · NPC',
      'Coordinador cívico · NPC',
    ),
    location: 'city-hall',
    color: '#7f8892',
    greeting: text(
      'The city keeps going while its community grows. No NPC votes. Just clear caretaker rules.',
      '社区慢慢成长，城市照常运转。NPC不会投票，基础服务按公开规则运行。',
      '社區慢慢成長，城市照常運轉。NPC不會投票，基礎服務按公開規則運行。',
      'La ciudad sigue funcionando mientras crece. Los NPC no votan: hay reglas de administración claras.',
    ),
  },
];

export function careerLevel(xp: number): number {
  return xp >= 300 ? 3 : xp >= 80 ? 2 : 1;
}
export function wage(xp: number): number {
  return [100, 125, 150][careerLevel(xp) - 1]!;
}
export function utcDay(now = new Date()): string {
  return now.toISOString().slice(0, 10);
}
export const getLocation = (id: string) => locations.find((x) => x.id === id);
export const getFurniture = (id: string) => furniture.find((x) => x.id === id);

export const goalDefinitions = [
  {
    id: 'first-shift',
    name: text('Your first paycheck', '第一份工资', '第一份薪水', 'Tu primer sueldo'),
    description: text('Complete one work shift.', '完成一次工作。', '完成一次工作。', 'Completa un turno.'),
  },
  {
    id: 'settle-in',
    name: text('Settle into the city', '在城市安顿下来', '在城市安頓下來', 'Instálate en la ciudad'),
    description: text(
      'Eat a meal and take a break.',
      '吃份饭，好好休息一次。',
      '吃份飯，好好休息一次。',
      'Come y descansa.',
    ),
  },
  {
    id: 'make-it-yours',
    name: text(
      'A place that feels like you',
      '布置属于自己的角落',
      '布置屬於自己的角落',
      'Un hogar con tu estilo',
    ),
    description: text(
      'Add a piece of furniture to your room.',
      '为房间添加一件家具。',
      '為房間添加一件家具。',
      'Añade un mueble a tu habitación.',
    ),
  },
  {
    id: 'city-explorer',
    name: text('Find your New York', '发现你的纽约', '發現你的紐約', 'Descubre tu Nueva York'),
    description: text(
      'Visit three different places.',
      '到访三个不同地点。',
      '到訪三個不同地點。',
      'Visita tres lugares diferentes.',
    ),
  },
  {
    id: 'good-neighbor',
    name: text('A good neighbor', '成为好邻居', '成為好鄰居', 'Un buen vecino'),
    description: text(
      'Help a neighbor or contribute to a project.',
      '帮邻居或参与公共项目。',
      '幫鄰居或參與公共專案。',
      'Ayuda a un vecino o a un proyecto.',
    ),
  },
  {
    id: 'next-chapter',
    name: text('Your next chapter', '走向下一站', '走向下一站', 'Tu siguiente capítulo'),
    description: text(
      'Reach career level two.',
      '任一职业晋升到二级。',
      '任一職業晉升到二級。',
      'Llega al nivel dos de una carrera.',
    ),
  },
  {
    id: 'open-door',
    name: text('Open for business', '开始营业', '開始營業', 'Abierto al público'),
    description: text(
      'Open your own neighborhood café.',
      '开一家属于自己的咖啡店。',
      '開一家屬於自己的咖啡店。',
      'Abre tu propia cafetería.',
    ),
  },
];

export const workProfiles: Record<
  SkillId,
  { xp: number; energy: number; relation?: { id: string; points: number } }[]
> = {
  service: [
    { xp: 12, energy: 20 },
    { xp: 8, energy: 14 },
    { xp: 8, energy: 18, relation: { id: 'maya', points: 2 } },
  ],
  logic: [
    { xp: 8, energy: 14 },
    { xp: 12, energy: 20 },
    { xp: 10, energy: 18 },
  ],
  creative: [
    { xp: 8, energy: 18, relation: { id: 'jordan', points: 2 } },
    { xp: 12, energy: 20 },
    { xp: 8, energy: 14 },
  ],
};
export const furnitureSize = (kind: string, rotation = 0) => {
  const width =
    kind === 'sofa'
      ? 1.8
      : kind === 'shelf'
        ? 1.3
        : kind === 'rug'
          ? 1.7
          : kind === 'plant'
            ? 0.7
            : kind === 'lamp'
              ? 0.6
              : 1;
  const depth = kind === 'rug' ? 1.2 : kind === 'table' ? 1 : 0.7;
  return rotation % 2 ? { width: depth, depth: width } : { width, depth };
};
